import { useState, useEffect } from 'react';
import { Plus, Upload, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminRentalsPage() {
  const [rentals, setRentals] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    client_id: '',
    rent_amount: '',
    start_date: ''
  });
  const [documentData, setDocumentData] = useState({
    name: '',
    url: ''
  });
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }

    setUploadingDocument(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Cloudinary returns full URL, use it directly
      const documentUrl = response.data.url;
      setDocumentData({
        name: response.data.filename,
        url: documentUrl
      });
      toast.success('Document chargé');
    } catch (error) {
      toast.error('Erreur lors de l\'upload du document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [rentalsRes, propertiesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/admin/rentals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties-for-rentals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRentals(rentalsRes.data);
      setProperties(propertiesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      const payload = {
        ...formData,
        rent_amount: parseFloat(formData.rent_amount),
        start_date: new Date(formData.start_date).toISOString()
      };

      await axios.post(`${API}/rentals`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Location ajoutée avec succès');
      setDialogOpen(false);
      setFormData({ property_id: '', client_id: '', rent_amount: '', start_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      await axios.put(
        `${API}/admin/rentals/${selectedRental.id}/documents`,
        documentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document ajouté');
      setDocumentDialogOpen(false);
      setDocumentData({ name: '', url: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du document');
    }
  };

  const handleDeleteDocument = async (rentalId, docIndex) => {
    if (!window.confirm('Supprimer ce document?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(
        `${API}/admin/rentals/${rentalId}/documents/${docIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Document supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div data-testid="loading-spinner" className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-rentals-page" className="min-h-screen pt-20 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="pt-12 flex items-center justify-between">
          <div className="space-y-4">
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight leading-none">Gestion Locative</h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground">Gérer les locations et documents clients</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-rental-button"
                onClick={() => setFormData({ property_id: '', client_id: '', rent_amount: '', start_date: '' })}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 text-xs uppercase tracking-widest font-semibold transition-all duration-300"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une location
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif">Ajouter une location</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} data-testid="rental-form" className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Bien</label>
                  <Select value={formData.property_id} onValueChange={(value) => setFormData({...formData, property_id: value})} required>
                    <SelectTrigger data-testid="rental-form-property">
                      <SelectValue placeholder="Sélectionner un bien" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(prop => (
                        <SelectItem key={prop.id} value={prop.id}>{prop.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Client</label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})} required>
                    <SelectTrigger data-testid="rental-form-client">
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>{client.full_name} ({client.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Loyer mensuel (€)</label>
                  <Input
                    data-testid="rental-form-amount"
                    type="number"
                    value={formData.rent_amount}
                    onChange={(e) => setFormData({...formData, rent_amount: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Date de début</label>
                  <Input
                    data-testid="rental-form-date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                  />
                </div>

                <Button
                  data-testid="rental-form-submit"
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold"
                >
                  Ajouter
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div data-testid="rentals-list" className="space-y-6">
          {rentals.map((rental) => (
            <div key={rental.id} data-testid={`rental-card-${rental.id}`} className="bg-white border border-border p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <h3 className="text-2xl font-serif mb-2">{rental.property?.title}</h3>
                  <p className="text-muted-foreground mb-2">Client: {rental.client?.full_name}</p>
                  <p className="text-muted-foreground">Loyer: €{rental.rent_amount}/mois</p>
                  <p className="text-sm text-muted-foreground">Début: {new Date(rental.start_date).toLocaleDateString('fr-BE')}</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">Documents</h4>
                    <Button
                      data-testid={`add-document-${rental.id}`}
                      size="sm"
                      onClick={() => {
                        setSelectedRental(rental);
                        setDocumentDialogOpen(true);
                      }}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                  </div>
                  {rental.documents && rental.documents.length > 0 ? (
                    <div className="space-y-2">
                      {rental.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span>{doc.name}</span>
                          <Button
                            data-testid={`delete-doc-${idx}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteDocument(rental.id, idx)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucun document</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Ajouter un document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddDocument} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Document PDF</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleDocumentUpload}
                className="hidden"
                id="document-upload"
                disabled={uploadingDocument}
              />
              <label 
                htmlFor="document-upload"
                className="cursor-pointer block"
              >
                <div className="w-full border-2 border-dashed border-border hover:border-primary transition-colors rounded p-6 text-center">
                  {uploadingDocument ? (
                    <span className="text-sm text-muted-foreground">Upload en cours...</span>
                  ) : documentData.name ? (
                    <div className="space-y-2">
                      <span className="text-sm font-semibold text-primary">✓ {documentData.name}</span>
                      <p className="text-xs text-muted-foreground">Cliquez pour changer</p>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">Cliquez pour sélectionner un PDF</span>
                  )}
                </div>
              </label>
            </div>
            <Button
              data-testid="document-submit"
              type="submit"
              disabled={!documentData.url || uploadingDocument}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold"
            >
              Ajouter le document
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminRentalsPage;